import './DynamicProfile.scss';
import { Extent, Map, MapView, SpatialReference } from 'cd-eft/utils/esriTypes';
import React, {useCallback, useEffect, useRef, useState } from 'react';
import { DynamicProfileStoreBase } from 'cd-eft/models/Stores/DynamicProfile/DynamicProfileStoreBase';
import stores from 'cd-eft/models/Stores';
import { mdiCircleMedium,mdiCrosshairsGps, mdiFormatListBulleted } from '@mdi/js';
import Icon from '@mdi/react';
import { Loader } from '../LoadingSpinner/LoadingSpinner';
import * as profileSymbolsAndLabels from 'cd-eft/models/Stores/DynamicProfile/profileSymbolsAndLabels';

const libraryStore = stores.AppStore.projectsLibraryStore;
const waterwayDesignStore = stores.AppStore.waterwayDesignStore;

type Type = 'threeCrossSection1' | 'threeCrossSection2' | 'threeCrossSection3';
interface IDynamicThreeCrossSectionProfileProps {
  profileStore: DynamicProfileStoreBase | undefined;
  editable: boolean;
  profileNumber: Type;
  generateInitialProfiles?;
}

const DynamicThreeCrossSectionProfile = (props: IDynamicThreeCrossSectionProfileProps) => {
  const [waitingInit, setWaitingInit] = useState(true)
  const [legendVisible, setLegendVisible] = useState(true);
  const [showCoordinates, setShowCoordinates] = useState(false);
  const profileMapViewRef = useRef<MapView>();
  if (!profileMapViewRef.current) {
    const spatialReference = new SpatialReference({ wkid: 102965 });
    const extent = new Extent({ xmin: 0, ymin: 0, xmax: 100, ymax: 110, spatialReference });

    profileMapViewRef.current = new MapView({ map: new Map({ layers: [] }), extent, spatialReference });
  }

  let id = '';
  switch (props.profileNumber) {
    case 'threeCrossSection1':
      id = 'Profile-TCS1';
      break;
    case 'threeCrossSection2':
      id = 'Profile-TCS2';
      break;
    case 'threeCrossSection3':
      id = 'Profile-TCS3';
      break;
    }

  useEffect(() => {
    if (profileMapViewRef.current) {
      if (waterwayDesignStore.uiState === "ReachDesignCalculate" || waterwayDesignStore.uiState === "ReachDesignSurface") {
        const sr: SpatialReference = libraryStore.projectSpatialReference!;
        props.profileStore?.initialize(profileMapViewRef.current, sr, props.editable, props.profileNumber);
        // setWaitingInit(false);

        profileMapViewRef.current.container = document.getElementById(id) as HTMLDivElement;
          if (props.profileStore) props.profileStore.startLengthyProcess = (state: boolean) => setWaitingInit(state);
        if (props.generateInitialProfiles) props.generateInitialProfiles();
      } else {
        setWaitingInit(false);
      }
    }
  }, [props.editable, props.profileStore]);

  const startCoordinateReadout = useCallback(() => {
    if (profileMapViewRef.current) {
      props.profileStore?.startCoordinateReadout();
    }
  }, [props.profileStore]);

  const stopCoordinateReadout = useCallback(() => {
    if (props.profileStore) {
      props.profileStore.stopCoordinateReadout();
    }
  }, []);

  const toggleCoordinates = () => {
    setShowCoordinates(prevState => !prevState);
  }

  useEffect(() => {
    if (showCoordinates) {
      startCoordinateReadout();
    } else {
      stopCoordinateReadout();
    }
  }, [showCoordinates]);

  return (
    <div>
      <div id={id} style={{ position: "relative" }}>
        <div id='tcs-widget'>
        <div id={`${props.profileNumber}-profile-coordinates-container`}></div>
          <div>
            <div title='Legend' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => setLegendVisible(!legendVisible)}><Icon path={mdiFormatListBulleted} size={0.9} horizontal/></div>
            <div title='Show Coordinates' className='esri-widget--button esri-widget esri-interactive home profile-btn' onClick={toggleCoordinates}><Icon path={mdiCrosshairsGps} size={0.8} /></div>
            <div title='Zoom In' className='esri-icon-zoom-in-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(0.75) }}></div>
            <div title='Zoom Out' className='esri-icon-zoom-out-fixed esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current) profileMapViewRef.current.extent = profileMapViewRef.current.extent.expand(1.25) }}></div>
            <div title='Zoom to Extent' className='esri-icon-applications esri-widget--button esri-widget esri-interactive home profile-btn' onClick={() => { if (profileMapViewRef.current && props.profileStore?.maxExtent) profileMapViewRef.current.extent = props.profileStore?.maxExtent }}></div>
          </div>
        </div>
        <div className="mt-5" hidden={!waitingInit}>
          <Loader message={'Loading profiles ...'} />
        </div>
      </div>
      <div className={`profile-legend ${legendVisible ? 'visible' : 'hidden'}`}>
        <div className='legend-row'>
          <Icon path={mdiCircleMedium} size={1} style={{ color: profileSymbolsAndLabels.netsLegendOrange }} />
          <div className='legend-text'>Design Surface</div>
        </div>
        <div className='legend-row'>
          <Icon path={mdiCircleMedium} size={1} style={{ color: profileSymbolsAndLabels.netsLegendPink}} />
          <div className='legend-text'>Original Surface</div>
        </div>
        <div className='legend-row'>
          <Icon path={mdiCircleMedium} size={1} style={{ color: profileSymbolsAndLabels.netsLegendLightBlue }} />
          <div className='legend-text'>Water Level</div>
        </div>
        <div className='legend-row'>
          <Icon path={mdiCircleMedium} size={1} style={{ color: profileSymbolsAndLabels.netsLegendBlack }} />
          <div className='legend-text'>Additional Details</div>
        </div>
      </div>
    </div>
  );
};

export { DynamicThreeCrossSectionProfile };
